import { dateToApiFormat } from '@/helpers/dateHelper';
import { getBaseRESTURL, useAuthenticationService } from '@/services/authentication/authentication-service';
import { useMyLocation } from '@/services/useLocations';
import { useCallback, useState } from 'react';
import strings from '@/pages/roster/roster.translations';

export interface OptimiserRequest {
  date: Date;
  templateId?: string;
}

export type OptimiserSolver = 'cpSat' | 'geneticAlgorithm';

export const useSendOptimiseRequest = (onComplete: () => void) => {
  const locationId = useMyLocation().id;
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const sendOptimization = useCallback(
    async (requestedDays: OptimiserRequest[], solver: OptimiserSolver = 'geneticAlgorithm') => {
      setSubmitLoading(true);
      setErrorMessage(undefined);
      const apiPath =
        solver === 'cpSat' ? 'OptimiseMultiDayTemplatedCpSat' : 'OptimiseMultiDayTemplated';
      const APIUrl = `${await getBaseRESTURL()}/Optimiser/${apiPath}`;
      const { user, getEffectiveTenantId } = useAuthenticationService.getState();

      const missingTemplateId = !requestedDays.every(d => d.templateId !== undefined);
      if (missingTemplateId) {
        throw Error('All days must have a template ID specified.');
      }

      const startDate = new Date(Math.min(...requestedDays.map(rd => rd.date.getTime())));
      const endDate = new Date(Math.max(...requestedDays.map(rd => rd.date.getTime())));

      const dateTemplates: string[] = requestedDays.map(patternDay => patternDay.templateId!);

      const request = {
        LocationId: locationId,
        Start: dateToApiFormat(startDate),
        End: dateToApiFormat(endDate),
        TemplateIDs: dateTemplates,
        SolverType: solver === 'cpSat' ? 1 : 0
      };

      try {
        const response = await fetch(APIUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.access_token}`,
            'tenant-id': getEffectiveTenantId() || ''
          },
          body: JSON.stringify(request)
        });

        if (response.ok) {
          onComplete();
        } else {
          setErrorMessage(strings.optimiseRosters.error.issueWithRequest);
        }
      } catch {
        setErrorMessage(strings.optimiseRosters.error.unableToSend);
      }

      setSubmitLoading(false);
    },
    [locationId, onComplete]
  );

  return { sendOptimization, loading: submitLoading, error: errorMessage };
};
